/* global __SERVER__ */

import React, { Component } from 'react';
import { func } from 'prop-types';
import { translate } from 'react-i18next';
import ReactMarkdown from 'react-markdown';

import { Button } from '@snapask/common-components';

import SEOHead from '@dragonfruit/components/SEOHead';
import MainLayout from '@dragonfruit/layouts/MainLayout';
import { isMobile } from '@dragonfruit/utils/detectMobile';

import SolarSystem from './components/SolarSystem';
import ScheduleSlideShow from './components/ScheduleSlideShow';
import InteractiveTimeline from './components/InteractiveTimeline';
import Feature from './components/Feature';
import CloudGradientSection from './components/CloudGradientSection';

import QuoteImage from './images/quotation.svg';
import TimImage from './images/tim.png';
import feature1 from './images/img_mission_value01.svg';
import feature2 from './images/img_mission_value02.svg';
import feature3 from './images/img_mission_value03.svg';

import styles from './Mission.scss';

import config from '@dragonfruit/config';
const { CAREER_WEBFLOW_DOMAIN } = config;

// Constant to decide how much
// to scroll.
const SENSITIVITY = 2;

const FEATURE_ICONS = [
  <img src={feature1} width="80px" />,
  <img src={feature2} width="80px" />,
  <img src={feature3} width="80px" />,
];

class Mission extends Component {
  static propTypes = {
    t: func.isRequired,
  };

  state = {
    rotate: 0,
  };

  componentDidMount() {
    if (!isMobile()) {
      window.addEventListener('scroll', this.handleScroll);
    }
  }

  componentWillUnmount() {
    if (!isMobile()) {
      window.removeEventListener('scroll', this.handleScroll);
    }
  }

  handleScroll = () => {
    const height = document.documentElement.offsetHeight;
    const scrollTop =
      window.pageYOffset ||
      (document.documentElement || document.body.parentNode || document.body)
        .scrollTop;

    const rotate = (scrollTop / (height * SENSITIVITY)) * 360;
    this.setState({
      rotate,
    });
  };

  renderSolutionStep = (solution, index) => (
    <div className={styles.solution} key={index}>
      <div className={styles.index}>
        <div className={styles.number}>{index + 1}</div>
      </div>
      <div className={styles.text}>{solution}</div>
    </div>
  );

  renderFeature = (feature, index) => (
    <Feature
      icon={FEATURE_ICONS[index]}
      title={feature.title}
      description={feature.description}
      key={index}
    />
  );

  render() {
    const { t } = this.props;
    const { rotate } = this.state;
    const SeoComponent = (
      <SEOHead
        path="mission"
        title={t('seo:seo.mission.title')}
        description={t('seo:seo.mission.description')}
      />
    );

    if (__SERVER__) {
      return SeoComponent;
    }

    const features = t('mission.sectionThree.features', {
      returnObjects: true,
      defaultValue: [],
    });

    const solutions = t('mission.sectionFive.solutions', {
      returnObjects: true,
      defaultValue: [],
    });

    const schedule = t('mission.sectionFour.schedule', {
      returnObjects: true,
      defaultValue: [],
    });

    return (
      <MainLayout showFooter>
        {SeoComponent}
        <div className={styles.root}>
          <section className={styles.sectionOne}>
            <div className={styles.container}>
              <h1
                dangerouslySetInnerHTML={{
                  __html: `${t('mission.jumbotron.title')}`,
                }}
              />
            </div>
          </section>
          <section className={styles.sectionTwo}>
            <div
              style={{
                transform: `rotate(${rotate}deg)`,
              }}
              className={styles.solarSystem}
            >
              <SolarSystem />
            </div>
            <div className={styles.container}>
              <h1>{t('mission.sectionTwo.title')}</h1>
              <div className={styles.description}>
                <ReactMarkdown source={t('mission.sectionTwo.description')} />
              </div>
            </div>
          </section>
          <section className={styles.sectionThree}>
            <div
              style={{
                transform: `rotate(${rotate + 10}deg)`,
              }}
              className={styles.solarSystem}
            >
              <SolarSystem />
            </div>
            <div className={styles.container}>
              <h2>{t('mission.sectionThree.title')}</h2>
              <div className={styles.features}>
                {features.map(this.renderFeature)}
              </div>
              <div className={styles.quote}>
                <img src={QuoteImage} />
                <p className={styles.content}>
                  {t('mission.sectionThree.quote.content')}
                </p>
                <img src={TimImage} width={60} className={styles.avatar} />
                <p className={styles.name}>
                  {t('mission.sectionThree.quote.author')}
                </p>
              </div>
            </div>
          </section>
          <section className={styles.sectionFour}>
            <div className={styles.container}>
              <h1>{t('mission.sectionFour.title')}</h1>
              <h4>{t('mission.sectionFour.subTitle')}</h4>
              <div className={styles.slideShowContainer}>
                <ScheduleSlideShow data={schedule} />
              </div>
            </div>
          </section>
          <section className={styles.sectionFive}>
            <CloudGradientSection>
              <div className={styles.container}>
                <div className={styles.text}>
                  <h1>{t('mission.sectionFive.title')}</h1>
                  <p>{t('mission.sectionFive.description')}</p>
                  <a
                    href={t('mission.sectionFive.ctaLink')}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Button
                      text={t('mission.sectionFive.cta')}
                      // theme={whiteWithBlueText}
                      secondary
                    />
                  </a>
                </div>
                <div className={styles.card}>
                  <div className={styles.content}>
                    {solutions.map(this.renderSolutionStep)}
                  </div>
                </div>
              </div>
            </CloudGradientSection>
          </section>
          <section className={styles.sectionSix}>
            <div className={styles.container}>
              <h1>{t('mission.sectionSix.title')}</h1>
              <div className={styles.storyContainer}>
                <InteractiveTimeline />
              </div>
            </div>
          </section>
          <section className={styles.sectionSeven}>
            <h4>{t('mission.sectionSeven.title')}</h4>
            <a href={CAREER_WEBFLOW_DOMAIN}>
              <Button text={t('mission.sectionSeven.cta')} primary />
            </a>
          </section>
        </div>
      </MainLayout>
    );
  }
}

export default translate(['mission', 'seo'])(Mission);



// WEBPACK FOOTER //
// ./app/containers/Mission/Mission.jsx